import { useState, useMemo } from 'react';
import api, { sanitizeProductLabel } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CULLING_PRODUCT, SOCIAL_MEDIA_PRODUCTS, HIGHLIGHT_MUSIC_ONLY, HIGHLIGHT_MUSIC_DIALOGUE, DOC_EDIT_PRODUCTS, NLE_OPTIONS } from '@/lib/pricing';
import { Loader2, ArrowRight } from 'lucide-react';

const slug = (s) => s.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();

const buildGroups = () => [
  { group: 'Highlight — Music Only', options: HIGHLIGHT_MUSIC_ONLY.map(r => ({ label: `Highlight — Music Only (${r.minutes} min)`, duration_minutes: r.minutes, price: r.price, editor_pay: r.editor_pay, turnaround_days: r.turnaround_days, is_doc_edit: false })) },
  { group: 'Highlight — Music + Dialogue', options: HIGHLIGHT_MUSIC_DIALOGUE.map(r => ({ label: `Highlight — Music + Dialogue (${r.minutes} min)`, duration_minutes: r.minutes, price: r.price, editor_pay: r.editor_pay, turnaround_days: r.turnaround_days, is_doc_edit: false })) },
  { group: 'Social Media Video', options: SOCIAL_MEDIA_PRODUCTS.map(p => ({ label: `${p.name} (${p.duration})`, duration_minutes: p.duration_minutes, price: p.price, editor_pay: p.editor_pay, turnaround_days: p.turnaround_days, is_doc_edit: false })) },
  { group: 'Doc Edits', options: DOC_EDIT_PRODUCTS.map(p => ({ label: p.name, price: p.price, editor_pay: p.editor_pay, turnaround_days: p.turnaround_days, is_doc_edit: true })) },
  { group: 'Culling Only', options: NLE_OPTIONS.map(nle => ({ label: `Culling Only (${nle})`, price: CULLING_PRODUCT.price, editor_pay: CULLING_PRODUCT.editor_pay, turnaround_days: CULLING_PRODUCT.turnaround_days, is_doc_edit: false })) },
];

export default function ChangeProductModal({ project, open, onOpenChange, onChanged }) {
  const groups = useMemo(buildGroups, []);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const currentLabel = project.line_items?.[0]?.label || project.product_name || '';
  const currentPrice = useMemo(() => {
    const found = groups.flatMap(g => g.options).find(o => o.label === currentLabel);
    return found ? found.price : null;
  }, [groups, currentLabel]);

  const specBody = (opt) => ({
    label: opt.label, duration_minutes: opt.duration_minutes ?? null,
    turnaround_days: opt.turnaround_days, editor_pay: opt.editor_pay, is_doc_edit: opt.is_doc_edit,
    price: opt.price, old_price: currentPrice,
  });

  const pick = async (opt) => {
    setSelected(opt); setPreview(null);
    if (opt.label === currentLabel) return;
    setLoading(true);
    try {
      const r = await api.put(`/projects/${project.id}/change-product`, { ...specBody(opt), preview: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
      setSelected(null);
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    if (!selected || !preview) return;
    setApplying(true);
    try {
      const r = await api.put(`/projects/${project.id}/change-product`, specBody(selected));
      toast.success(`Edit type changed to ${selected.label}`, { description: `${r.data.client_message} · ${r.data.payment_message}`, duration: 10000 });
      onChanged?.();
      onOpenChange(false);
      setSelected(null); setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Change failed');
    } finally { setApplying(false); }
  };

  const fmt$ = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setSelected(null); setPreview(null); } }}>
      <DialogContent className="max-w-lg bg-ug-surface border-ug-border p-0 gap-0" data-testid="change-product-modal">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-ug-border-subtle">
          <DialogTitle className="font-sans text-sm text-zinc-100">Change edit type</DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500 font-sans">
            Current: <span className="text-ug-gold">{sanitizeProductLabel(currentLabel, 'admin')}</span> · client billing stays frozen — editor pay follows the new product.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col max-h-[70vh]">
          <div className="overflow-y-auto max-h-64 px-3 py-2" data-testid="change-product-options">
            {groups.map(g => (
              <div key={g.group} className="mb-2">
                <div className="px-2 py-1 text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500">{g.group}</div>
                {g.options.map(opt => {
                  const isCurrent = opt.label === currentLabel;
                  const isSel = selected?.label === opt.label;
                  return (
                    <button key={opt.label} disabled={isCurrent} onClick={() => pick(opt)}
                      data-testid={`cp-option-${slug(opt.label)}`}
                      className={`w-full flex items-center justify-between px-3 py-1.5 rounded-md text-left transition-colors ${isSel ? 'bg-ug-gold/15 ring-1 ring-ug-gold/40' : isCurrent ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/[0.04]'}`}>
                      <span className="text-[11px] text-zinc-100">{opt.label}{isCurrent ? ' (current)' : ''}</span>
                      <span className="text-[9px] font-mono text-slate-500 shrink-0 ml-2">{opt.turnaround_days}d SLA</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="border-t border-ug-border-subtle px-5 py-3 min-h-[88px]" data-testid="change-product-preview">
            {loading && <div className="flex items-center gap-2 text-[11px] text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Computing impact…</div>}
            {!loading && !preview && <p className="text-[11px] text-slate-600 font-sans">Select a new product to preview the impact before confirming.</p>}
            {!loading && preview && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-[11px] font-sans text-zinc-100">
                  <span className="truncate">{preview.old_product}</span><ArrowRight className="w-3 h-3 text-ug-gold shrink-0" /><span className="truncate text-ug-gold">{preview.new_product}</span>
                </div>
                <p className="text-[10px] font-sans text-slate-400" data-testid="cp-preview-sla">
                  Turnaround: {preview.sla_before ?? '—'}d → <span className="text-zinc-100">{preview.sla_after}d</span> · due date will <span className="text-zinc-100">NOT</span> move{preview.editor_due_date ? ` (stays ${preview.editor_due_date})` : ''} — adjust deliberately via the due-date flow if needed.
                </p>
                <p className="text-[10px] font-sans text-slate-400" data-testid="cp-preview-pay">
                  Editor pay: {fmt$(preview.editor_pay_before)} → <span className="text-zinc-100">{fmt$(preview.editor_pay_after)}</span>
                </p>
                <p className={`text-[10px] font-sans font-semibold ${preview.client_action === 'none' ? 'text-slate-400' : 'text-ug-gold'}`} data-testid="cp-preview-client-delta">
                  {preview.client_message}
                </p>
                <p className={`text-[10px] font-sans ${preview.payment_action === 'paid_warning' || preview.payment_action === 'manual_override_skipped' ? 'text-ug-status-red' : 'text-slate-400'}`} data-testid="cp-preview-payment">
                  {preview.payment_message}
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 px-5 pb-4">
            <button onClick={() => onOpenChange(false)} data-testid="change-product-cancel"
              className="px-3 py-1.5 rounded-lg text-[11px] font-sans font-semibold text-slate-400 hover:text-zinc-100 transition-colors">Cancel</button>
            <button onClick={confirm} disabled={!preview || applying} data-testid="change-product-confirm"
              className="px-4 py-1.5 rounded-lg text-[11px] font-sans font-semibold text-obsidian bg-ug-gold hover:bg-ug-gold-bright disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5">
              {applying && <Loader2 className="w-3 h-3 animate-spin" />} Confirm change
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
